import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ArrowLeft, FileText, Loader2, DollarSign, Package } from "lucide-react";
import Navbar from "@/components/Navbar";
import { AppLayout } from "@/components/layout/AppLayout";
import { useAuth } from "@/contexts/AuthContext";
import { useMyPayeeTargets } from "@/hooks/useMyPayeeTargets";
import {
  useMyPayeeInvoices,
  pendingInvoices,
  paidInvoices,
  type MyPayeeInvoiceRow,
} from "@/hooks/useMyPayeeInvoices";
import { useInvoiceWithItems } from "@/hooks/useInvoices";
import { usePayoutStatus } from "@/hooks/usePayoutStatus";
import { formatMoney } from "@/lib/formatMoney";
import { format, parseISO } from "date-fns";
import type { InvoiceItemRow } from "@/hooks/useInvoices";

function PaymentCard({
  invoice,
  propertyName,
  onSelect,
}: {
  invoice: MyPayeeInvoiceRow;
  propertyName?: string | null;
  onSelect: () => void;
}) {
  const isPaid = invoice.status === "paid" || invoice.paid_at != null;
  const currency = "CAD";
  const subtotalPaidToPayeeCents = Number(invoice.total_amount_cents) || 0;

  return (
    <Card
      className="cursor-pointer touch-manipulation active:opacity-90"
      onClick={onSelect}
    >
      <CardContent className="p-4 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm">Invoice</p>
          <p className="text-xs text-muted-foreground truncate">
            {format(parseISO(invoice.period_start), "MMM d, yyyy")} –{" "}
            {format(parseISO(invoice.period_end), "MMM d, yyyy")}
            {propertyName ? ` · ${propertyName}` : ""}
          </p>
          <Badge
            variant={isPaid ? "default" : "secondary"}
            className="mt-1.5 text-xs"
          >
            {isPaid ? "Paid" : "Pending"}
          </Badge>
        </div>
        <div className="text-right shrink-0">
          <div className="font-medium">
            {formatMoney(subtotalPaidToPayeeCents, currency)}
          </div>
          <div className="text-xs text-muted-foreground">paid to you</div>
        </div>
      </CardContent>
    </Card>
  );
}

function InvoiceDetailSheet({
  invoiceId,
  open,
  onOpenChange,
}: {
  invoiceId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const { invoice, items, isLoading } = useInvoiceWithItems(invoiceId ?? undefined);
  const currency = "CAD";

  if (!invoiceId) return null;

  const cleaningItems = (items ?? []).filter((i) => i.item_type === "cleaning");
  const taxItems = (items ?? []).filter(
    (i) => i.item_type === "manual" && (i.meta as { kind?: string } | null)?.kind === "tax"
  );
  const reimbursementItems = (items ?? []).filter(
    (i) =>
      i.item_type === "inventory_reimbursement" || i.item_type === "cleaner_purchase"
  );
  const totalCents = (items ?? []).reduce((s, i) => s + Number(i.amount_cents), 0);
  const subtotalPaidToPayeeCents =
    Number((invoice as { subtotal_amount_cents?: number | null }).subtotal_amount_cents) ||
    totalCents ||
    0;
  const processingFeeCents =
    Number((invoice as { processing_fee_amount_cents?: number | null }).processing_fee_amount_cents) || 0;
  const totalChargedToOwnerCents =
    Number((invoice as { amount_to_charge_cents?: number | null }).amount_to_charge_cents) ||
    subtotalPaidToPayeeCents + processingFeeCents;
  const isPaid = invoice?.status === "paid" || (invoice as { paid_at?: string })?.paid_at != null;
  const paidAt = (invoice as { paid_at?: string })?.paid_at;
  const stripePiId = (invoice as { stripe_payment_intent_id?: string })?.stripe_payment_intent_id;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Invoice</SheetTitle>
          <SheetDescription>
            {invoice
              ? `${format(parseISO(invoice.period_start), "MMM d, yyyy")} – ${format(parseISO(invoice.period_end), "MMM d, yyyy")}`
              : "—"}
          </SheetDescription>
        </SheetHeader>
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}
        {!isLoading && invoice && (
          <div className="space-y-4 pt-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Invoice ID</p>
              <p className="text-sm font-mono">{invoice.id}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                <DollarSign className="h-3 w-3" /> Cleaning fees
              </p>
              {cleaningItems.length === 0 ? (
                <p className="text-sm text-muted-foreground">None</p>
              ) : (
                <ul className="space-y-1">
                  {cleaningItems.map((line: InvoiceItemRow) => (
                    <li
                      key={line.id}
                      className="flex justify-between text-sm"
                    >
                      <span className="truncate pr-2">{line.description || "—"}</span>
                      <span>{formatMoney(Number(line.amount_cents), currency)}</span>
                    </li>
                  ))}
                </ul>
              )}
              {taxItems.length > 0 && (
                <div className="pt-2">
                  <p className="text-xs text-muted-foreground mb-1">Tax</p>
                  <ul className="space-y-1">
                    {taxItems.map((line: InvoiceItemRow) => (
                      <li key={line.id} className="flex justify-between text-sm">
                        <span className="truncate pr-2">{line.description || "Tax"}</span>
                        <span>{formatMoney(Number(line.amount_cents), currency)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                <Package className="h-3 w-3" /> Inventory reimbursements
              </p>
              {reimbursementItems.length === 0 ? (
                <p className="text-sm text-muted-foreground">None</p>
              ) : (
                <ul className="space-y-1">
                  {reimbursementItems.map((line: InvoiceItemRow) => (
                    <li
                      key={line.id}
                      className="flex justify-between text-sm"
                    >
                      <span className="truncate pr-2">{line.description || "—"}</span>
                      <span>{formatMoney(Number(line.amount_cents), currency)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="border-t pt-3 space-y-1">
              <div className="flex justify-between font-medium">
                <span>Subtotal (paid to payee)</span>
                <span>{formatMoney(subtotalPaidToPayeeCents, currency)}</span>
              </div>
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>Processing fee (paid by owner)</span>
                <span>{formatMoney(processingFeeCents, currency)}</span>
              </div>
              <div className="flex justify-between font-medium">
                <span>Total charged to owner</span>
                <span>{formatMoney(totalChargedToOwnerCents, currency)}</span>
              </div>
              <p className="text-xs text-muted-foreground pt-1">
                Owners cover processing fees so cleaners receive the full amount owed.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={isPaid ? "default" : "secondary"}>
                {isPaid ? "Paid" : invoice.status}
              </Badge>
            </div>
            {isPaid && paidAt && (
              <div className="text-sm text-muted-foreground">
                <p>Paid: {format(parseISO(paidAt), "MMM d, yyyy HH:mm")}</p>
                {stripePiId && (
                  <p className="font-mono text-xs mt-1">{stripePiId}</p>
                )}
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  onOpenChange(false);
                  navigate(`/properties/${invoice.property_id}?tab=invoices&invoiceId=${invoice.id}`);
                }}
              >
                <FileText className="h-4 w-4 mr-2" />
                Open invoice
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default function PaymentsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get("invoiceId");
  const { user, profile, loading: authLoading } = useAuth();
  const isPayee = profile?.role === "cleaner" || profile?.role === "company_admin";
  const { data: targets, isSuccess: targetsReady } = useMyPayeeTargets();
  const { data: allInvoices = [], isLoading: invoicesLoading } = useMyPayeeInvoices();
  const { state: payoutState } = usePayoutStatus();
  const [sheetOpen, setSheetOpen] = useState(false);

  const pending = pendingInvoices(allInvoices);
  const paid = paidInvoices(allInvoices);
  const outstandingCents = pending.reduce((sum, inv) => sum + Number(inv.total_amount_cents ?? 0), 0);
  const hasNoTargets = targetsReady && !targets?.cleanerId && (!targets?.companyIds?.length);

  useEffect(() => {
    if (!authLoading && !user) navigate("/login", { replace: true });
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!authLoading && user && profile && !isPayee) navigate("/account", { replace: true });
  }, [authLoading, user, profile, isPayee, navigate]);

  useEffect(() => {
    if (selectedId) setSheetOpen(true);
  }, [selectedId]);

  const openSheet = (id: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("invoiceId", id);
      return next;
    });
    setSheetOpen(true);
  };

  const closeSheet = () => {
    setSheetOpen(false);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("invoiceId");
      return next;
    });
  };

  if (authLoading || !user) return null;
  if (profile && !isPayee) return null;

  return (
    <AppLayout>
      <Navbar />
      <div className="w-full max-w-[560px] mx-auto px-4 sm:px-6 py-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/account/payouts")}
          className="mb-4 -ml-2"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        <div className="space-y-2 mb-6">
          <h1 className="text-xl font-semibold">Payments</h1>
          <p className="text-sm text-muted-foreground">
            Track what&apos;s pending and what&apos;s been paid.
          </p>
        </div>

        {!hasNoTargets && outstandingCents > 0 && (
          <Card className="mb-4 border-primary/30 bg-primary/5">
            <CardContent className="py-4">
              <p className="text-sm text-muted-foreground">Outstanding (owed to you)</p>
              <p className="text-2xl font-semibold">{formatMoney(outstandingCents, "CAD")}</p>
              <p className="text-xs text-muted-foreground mt-1">
                From {pending.length} pending invoice{pending.length !== 1 ? "s" : ""}. Connect Stripe in Payouts to receive payments when owners pay.
              </p>
            </CardContent>
          </Card>
        )}

        {!hasNoTargets && outstandingCents === 0 && pending.length === 0 && paid.length > 0 && (
          <Card className="mb-4 border-green-200 bg-green-50/50 dark:border-green-900/20 dark:bg-green-950/20">
            <CardContent className="py-4">
              <p className="text-sm font-medium text-green-800 dark:text-green-200">You&apos;re all caught up</p>
              <p className="text-xs text-muted-foreground mt-0.5">No outstanding invoices. New payments will appear under Paid.</p>
            </CardContent>
          </Card>
        )}

        {hasNoTargets && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">No payments yet</CardTitle>
              <CardDescription>
                Invoices will appear here when a property owner adds you as their cleaner or company and generates invoices for you.
              </CardDescription>
            </CardHeader>
          </Card>
        )}

        {!hasNoTargets && payoutState !== "complete" && payoutState !== "not_started" && (
          <Card className="mb-4 border-amber-200 bg-amber-50/50 dark:border-amber-900 dark:bg-amber-950/20">
            <CardContent className="py-4">
              <p className="text-sm">
                Finish Stripe setup to receive payouts.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => navigate("/account/payouts")}
              >
                Go to Payouts
              </Button>
            </CardContent>
          </Card>
        )}

        {!hasNoTargets && (
          <Tabs defaultValue="pending" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="pending">Pending</TabsTrigger>
              <TabsTrigger value="paid">Paid</TabsTrigger>
            </TabsList>
            <TabsContent value="pending" className="mt-4 space-y-3">
              {invoicesLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : pending.length === 0 ? (
                <div className="text-sm text-muted-foreground py-6 text-center space-y-2">
                  <p>No pending invoices.</p>
                  <p className="text-xs max-w-sm mx-auto">
                    Invoices appear here after the property owner creates them (Payments → Create invoice for date range) or when they use &quot;Per cleaning&quot; and a cleaning is completed. If you expect payment, ask the owner to create an invoice for the relevant dates.
                  </p>
                </div>
              ) : (
                pending.map((inv) => (
                  <PaymentCard
                    key={inv.id}
                    invoice={inv}
                    onSelect={() => openSheet(inv.id)}
                  />
                ))
              )}
            </TabsContent>
            <TabsContent value="paid" className="mt-4 space-y-3">
              {invoicesLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : paid.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  No paid invoices yet.
                </p>
              ) : (
                paid.map((inv) => (
                  <PaymentCard
                    key={inv.id}
                    invoice={inv}
                    onSelect={() => openSheet(inv.id)}
                  />
                ))
              )}
            </TabsContent>
          </Tabs>
        )}

        <InvoiceDetailSheet
          invoiceId={selectedId}
          open={sheetOpen}
          onOpenChange={(open) => !open && closeSheet()}
        />
      </div>
    </AppLayout>
  );
}
